import React from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

// Loading spinner component
const LoadingSpinner = ({ size = "sm" }) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <svg className={`animate-spin ${sizeClasses[size]}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
};

const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, onLimitChange, showInfo = true, itemName = "items", className = "", isLoading = false, limitOptions = [5, 10, 25, 50, 100] }) => {
  const darkMode = useSelector(selectDarkMode);

  // Calculate showing range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1 && !onLimitChange) return null;

  const handlePrevious = () => {
    if (currentPage > 1 && !isLoading) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages && !isLoading) onPageChange(currentPage + 1);
  };

  const handlePageClick = (page) => {
    if (!isLoading && page !== currentPage) {
      onPageChange(page);
    }
  };

  const renderPages = () => {
    const pages = [];

    // Handle 2 or fewer pages
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageClick(i)}
            disabled={isLoading}
            className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${currentPage === i ? "bg-blue-600 text-white" : darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {i}
          </button>,
        );
      }
      return pages;
    }

    // More than 2 pages - show first, current area, and last
    const showPages = [];

    // Always show first page
    showPages.push(1);

    // Show pages around current
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!showPages.includes(i)) {
        showPages.push(i);
      }
    }

    // Always show last page
    if (!showPages.includes(totalPages)) {
      showPages.push(totalPages);
    }

    // Sort pages
    showPages.sort((a, b) => a - b);

    // Render pages with ellipsis
    let lastPage = 0;
    showPages.forEach((page) => {
      // Add ellipsis if there's a gap
      if (page - lastPage > 1) {
        pages.push(
          <span key={`ellipsis-${page}`} className={`px-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            ...
          </span>,
        );
      }

      pages.push(
        <button
          key={page}
          onClick={() => handlePageClick(page)}
          disabled={isLoading}
          className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${currentPage === page ? "bg-blue-600 text-white" : darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {page}
        </button>,
      );

      lastPage = page;
    });

    return pages;
  };

  return (
    <div className={`px-6 py-3 border-t rounded-b-xl ${darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"} ${className}`}>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Left side: Records per page selector and info */}
        {/* Right side: Page navigation */}
        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1 || isLoading}
              className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                darkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              } ${isLoading ? "pointer-events-none" : ""}`}
            >
              {isLoading && currentPage > 1 ? <LoadingSpinner size="sm" /> : <ChevronLeftIcon className="h-4 w-4" />}
            </button>

            {/* Page Numbers */}
            <div className={`flex items-center space-x-1 ${isLoading ? "opacity-70" : ""}`}>{renderPages()}</div>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages || isLoading}
              className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                darkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              } ${isLoading ? "pointer-events-none" : ""}`}
            >
              {isLoading && currentPage < totalPages ? <LoadingSpinner size="sm" /> : <ChevronRightIcon className="h-4 w-4" />}
            </button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* Records per page selector */}
          {onLimitChange && (
            <div className="flex items-center gap-2">
              <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                disabled={isLoading}
                className={`px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {limitOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>per page</span>
            </div>
          )}

          {/* Showing info */}
          {showInfo && (
            <div className={`text-sm flex items-center gap-2 ${darkMode ? "text-gray-400" : "text-gray-700"}`}>
              {isLoading && <LoadingSpinner size="sm" />}
              <span>
                Showing {startItem}-{endItem} of {totalItems} {itemName}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;
